import cx from "classnames";
import { Alert } from "components/common/Alert/Alert";
import React, { useEffect, useRef } from "react";
import { Field, FieldProps, FieldRenderProps } from "react-final-form";
import st from "./FieldInput.module.scss";

export interface FieldInputProps<T = string>
  extends FieldProps<T, FieldRenderProps<T, HTMLElement>, HTMLElement> {
  autocompleteValue?: string;
  maxLength?: number;
  browserAutofillOffValue?: string;
  inputClassName?: string;
  label?: string;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  placeholder?: string;
  children?: React.ReactNode[];
}

type OnChangeType = (event: React.ChangeEvent<HTMLInputElement>) => void;

export function FieldInput<
  T extends string | number | readonly string[] | undefined | null,
>({
  name,
  defaultValue,
  autocompleteValue,
  browserAutofillOffValue,
  maxLength,
  inputClassName,
  placeholder,
  label,
  onClick,
  ...inputProps
}: FieldInputProps<T>): React.ReactElement {
  const inputOnChange = useRef<OnChangeType | null>(null);

  useEffect(() => {
    if (inputOnChange.current && autocompleteValue !== undefined) {
      inputOnChange.current(
        autocompleteValue as unknown as React.ChangeEvent<HTMLInputElement>,
      );
    }
  }, [autocompleteValue, inputOnChange]);

  return (
    <Field<T> name={name} defaultValue={defaultValue} {...inputProps}>
      {({ input, meta }) => {
        if (!inputOnChange.current) {
          inputOnChange.current = input.onChange as unknown as OnChangeType;
        }

        const hasError = meta.touched && meta.error;

        return (
          <div className={cx(st.inputContainer, inputClassName)}>
            {label && (
              <label htmlFor={name} className={st.label}>
                {label}
              </label>
            )}
            <input
              id={name}
              type="text"
              {...input}
              ref={input.inputRef}
              value={input.value && input.value.toString()}
              placeholder={placeholder}
              autoComplete={browserAutofillOffValue}
              maxLength={maxLength}
              onClick={onClick}
              className={cx(st.fieldInput, hasError && st.error)}
            />
            {hasError && (
              <Alert hideBackground type="error">
                {meta.error}
              </Alert>
            )}
          </div>
        );
      }}
    </Field>
  );
}
